import React, { FC, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import Loader from '../../components/loader/index';
import { QueryKey } from '../../constants/index';
import {
  CompanyHooks,
  useInvalidateQueries,
  useModalState,
  useQueryParams,
  useScreenSize,
} from '../../hooks/index';
import { companiesSortStore } from '../../storage/companies-sort-storage.util';
import {
  CompanyAllFreqFilter,
  CompanyAllPaymentFilter,
  CompanyAllPlanFilter,
  CompanyOrder,
  CompanySortBy,
  CompanyTabType,
  NumberOfFetchedItems,
  SelectNumberOfFetchedItems,
} from '../../types/company.types';
import { OfferSortBy, SubscriptionOffer } from '../../types/offer.types';
import { BulkActionsModal } from './bullk-actions-modal/bulk-actions-modal';
import { CompanyHeader } from './company-header';
import { MobileFilterModal } from './company-mobile/index';
import { CompanyTab } from './company-tab/index';
import { CreateSubscriptionModal, SubscriptionModalMode } from './create-subscription-modal/index';
import { DeleteSubscriptionModal } from './delete-subscription-modal/index';
import { EditSubscriptionModal } from './edit-subscription-modal/index';
import { SubscriptionTab } from './subscription-tab/index';
import { ViewCompanyModal } from './view-company-modal/index';

const Companies: FC = () => {
  const { invalidateQuery } = useInvalidateQueries(QueryKey.GET_COMPANIES, QueryKey.GET_COMPANY);
  const { get } = useQueryParams();
  const { width } = useScreenSize();
  const isMobile = width <= 1023;

  const [selectedTab, setTab] = useState<CompanyTabType>(CompanyTabType.ALL);
  const [search, setSearch] = useState<null | string>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numberOfFetchedItems, setNumberOfFetchedItems] = useState<SelectNumberOfFetchedItems>(
    NumberOfFetchedItems.TEN,
  );

  const [selectedFreqFilter, setSelectedFreqFilter] = useState<CompanyAllFreqFilter>('all');
  const [selectedPlanFilter, setSelectedPlanFilter] = useState<CompanyAllPlanFilter>('all');
  const [selectedPaymentFilter, setSelectedPaymentFilter] =
    useState<CompanyAllPaymentFilter>('all');
  const [selectedFilters, setSelectedFilters] = useState<{
    frequency: CompanyAllFreqFilter;
    paymentStatus: CompanyAllPaymentFilter;
    planType: CompanyAllPlanFilter;
  }>({
    planType: 'all',
    frequency: 'all',
    paymentStatus: 'all',
  });

  const handleSearchChange = (state: null | string) => {
    setSearch(state);
    setCurrentPage(1);
  };
  const handlePlanFilterChange = (state: CompanyAllPlanFilter) => {
    setCurrentPage(1);
    setSelectedPlanFilter(state);
  };
  const handleFreqFilterChange = (state: CompanyAllFreqFilter) => {
    setCurrentPage(1);
    setSelectedFreqFilter(state);
  };
  const handlePaymentFilterChange = (state: CompanyAllPaymentFilter) => {
    setCurrentPage(1);
    setSelectedPaymentFilter(state);
  };
  const handleTabChange = (state: CompanyTabType) => {
    setCurrentPage(1);
    setTab(state);
  };
  const handleSelectedFiltersChange = (state: {
    frequency: CompanyAllFreqFilter;
    paymentStatus: CompanyAllPaymentFilter;
    planType: CompanyAllPlanFilter;
  }) => {
    setCurrentPage(1);
    setSelectedFilters(state);
  };

  const [selectedCompaniesIds, setSelectedCompaniesIds] = useState<string[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<null | string>(null);
  const [sortState, setSortState] = useState<{
    field: CompanySortBy | null;
    sortMode: CompanyOrder | null;
  }>(companiesSortStore.get() || { field: null, sortMode: null });

  const { companies, totalPages } = CompanyHooks.useCompanies({
    take: Number(numberOfFetchedItems),
    currentPage,
    search: search || undefined,
    orderBy: sortState.field || undefined,
    orderMode: sortState.sortMode || undefined,
    planType: isMobile ? selectedFilters.planType : selectedPlanFilter,
    planFrequency: isMobile ? selectedFilters.frequency : selectedFreqFilter,
    subscriptionStatus: isMobile ? selectedFilters.paymentStatus : selectedPaymentFilter,
  });

  const { companies: filteredCompanies, totalPages: filteredTotalPages } =
    CompanyHooks.useCompanies({
      take: Number(numberOfFetchedItems),
      currentPage,
      search: search || undefined,
      orderBy: sortState.field || undefined,
      orderMode: sortState.sortMode || undefined,
      planType: isMobile ? selectedFilters.planType : selectedPlanFilter,
      planFrequency: isMobile ? selectedFilters.frequency : selectedFreqFilter,
      subscriptionStatus: isMobile ? selectedFilters.paymentStatus : selectedPaymentFilter,
      isEnterpriseRequested: true,
    });

  const isSubscriptionTab = selectedTab === CompanyTabType.SUBSCRIPTION;

  const [offerSortState, setOfferSortState] = useState<{
    field: null | OfferSortBy;
    sortMode: CompanyOrder | null;
  }>({ field: null, sortMode: null });

  const handleOfferSortField = useCallback((newField: OfferSortBy) => {
    setOfferSortState((prevState) => {
      if (prevState.field === newField) {
        const nextSortMode = !prevState.sortMode
          ? CompanyOrder.ASC
          : prevState.sortMode === CompanyOrder.ASC
            ? CompanyOrder.DESC
            : null;

        return { ...prevState, sortMode: nextSortMode };
      }

      return { field: newField, sortMode: CompanyOrder.ASC };
    });
  }, []);

  const {
    offers,
    totalPages: offerTotalPages,
    invalidateQuery: invalidateOffers,
  } = CompanyHooks.useSubscriptionOffers({
    take: Number(numberOfFetchedItems),
    currentPage,
    search: search || undefined,
    planType: isMobile ? selectedFilters.planType : selectedPlanFilter,
    planFrequency: isMobile ? selectedFilters.frequency : selectedFreqFilter,
    orderBy: offerSortState.sortMode ? (offerSortState.field ?? undefined) : undefined,
    orderMode: offerSortState.sortMode || undefined,
    enabled: isSubscriptionTab,
  });

  const { deleteSubscriptionOfferAsync, isPending: isDeletingOffer } =
    CompanyHooks.useDeleteSubscriptionOffer();

  const [selectedOffer, setSelectedOffer] = useState<null | SubscriptionOffer>(null);
  const [offerModalMode, setOfferModalMode] = useState<SubscriptionModalMode>('view');
  const [offerToDelete, setOfferToDelete] = useState<null | SubscriptionOffer>(null);

  const { deactivateCompany, isPending } = CompanyHooks.useDeactivate();

  const handleBulkDeactivate = async (companyIds: string[]) => {
    let hasError = false;

    for (const companyId of companyIds) {
      try {
        await new Promise<void>((resolve, reject) => {
          deactivateCompany(
            { companyId },
            {
              onSuccess: () => {
                resolve();
              },
              onError: (error) => {
                reject(error);
              },
            },
          );
        });
      } catch (error) {
        hasError = true;
      }
    }

    if (hasError) {
      toast.error('Error deactivating some companies');
    } else {
      invalidateQuery();
      setSelectedCompanyId(null);
      setSelectedCompaniesIds([]);
      toast.success(`${companyIds.length} companies have been deactivated`);
    }
  };

  const handleSelectNumberOfFetchedItems = useCallback(
    (select: SelectNumberOfFetchedItems) => {
      setNumberOfFetchedItems(select);
      invalidateQuery();
    },
    [invalidateQuery],
  );

  const handleSortField = useCallback(
    (newField: CompanySortBy) => {
      setSortState((prevState) => {
        if (prevState.field === newField) {
          const nextSortMode = !prevState.sortMode
            ? CompanyOrder.ASC
            : prevState.sortMode === CompanyOrder.ASC
              ? CompanyOrder.DESC
              : null;

          companiesSortStore.set({ ...prevState, sortMode: nextSortMode });
          return { ...prevState, sortMode: nextSortMode };
        } else {
          companiesSortStore.set({
            field: newField,
            sortMode: CompanyOrder.ASC,
          });
          return { field: newField, sortMode: CompanyOrder.ASC };
        }
      });
      invalidateQuery();
    },
    [invalidateQuery],
  );

  const {
    isOpen: isOpenBulkActionsModal,
    handleDismissModal: handleDismissBulkActionsModal,
    handleOpenModal: handleOpenBulkActionsModal,
  } = useModalState({
    onSuccess: () => setSelectedCompaniesIds([]),
  });

  const {
    handleCloseModal: handleCloseViewCompanyModal,
    handleOpenModal: handleOpenViewCompanyModal,
    isOpen: isOpenViewCompanyModal,
  } = useModalState({});

  const {
    handleCloseModal: handleCloseEditSubscriptionModal,
    handleOpenModal: handleOpenEditSubscriptionModal,
    isOpen: isOpenEditSubscriptionModal,
  } = useModalState({ initialValue: !!get('openEditModal') });

  const {
    handleCloseModal: handleCloseCreateSubscriptionModal,
    handleOpenModal: handleOpenCreateSubscriptionModal,
    isOpen: isOpenCreateSubscriptionModal,
  } = useModalState({});

  const {
    handleCloseModal: handleCloseOfferModal,
    handleOpenModal: handleOpenOfferModal,
    isOpen: isOpenOfferModal,
  } = useModalState({});

  const handleViewOffer = (offer: SubscriptionOffer) => {
    setSelectedOffer(offer);
    setOfferModalMode('view');
    handleOpenOfferModal();
  };

  const handleEditOffer = (offer: SubscriptionOffer) => {
    setSelectedOffer(offer);
    setOfferModalMode('edit');
    handleOpenOfferModal();
  };

  const handleConfirmDeleteOffer = async () => {
    if (!offerToDelete) return;

    try {
      await deleteSubscriptionOfferAsync({ id: offerToDelete.id });
      toast.success('Subscription offer deleted');
      // Deleting the only offer on a page would leave `skip` pointing past the shrunken list —
      // the refetch would render an empty page while offers still exist on earlier pages.
      if (offers.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
      invalidateOffers();
      setOfferToDelete(null);
    } catch {
      // error toast handled by useAppMutation default error handling
    }
  };

  const {
    isOpen: isOpenMobileFilterModal,
    toggler: togglerMobileFilterModal,
    handleOpenModal: handleOpenMobileFilterModal,
    handleCloseModal: handleCloseMobileFilterModal,
    handleSuccessModal: handleSuccessMobileFilterModal,
  } = useModalState();

  const handleResetFilters = () => {
    handleSelectedFiltersChange({
      planType: 'all',
      frequency: 'all',
      paymentStatus: 'all',
    });
    handlePlanFilterChange('all');
    handleFreqFilterChange('all');
    handlePaymentFilterChange('all');
  };

  const handleApplyFilters = () => {
    handleSelectedFiltersChange({
      planType: selectedPlanFilter,
      frequency: selectedFreqFilter,
      paymentStatus: selectedPaymentFilter,
    });
    handleSuccessMobileFilterModal();
  };

  useEffect(() => {
    if (selectedCompaniesIds.length !== 0) return handleOpenBulkActionsModal();
    handleDismissBulkActionsModal();
  }, [handleDismissBulkActionsModal, handleOpenBulkActionsModal, selectedCompaniesIds.length]);

  useEffect(() => {
    if (isMobile) {
      setNumberOfFetchedItems(NumberOfFetchedItems.TEN);
    }
  }, [isMobile]);

  useEffect(() => {
    const isOpen = !!get('openEditModal');
    const companyId = get('companyId');

    if (isOpen && companyId) {
      handleOpenEditSubscriptionModal();
    } else {
      handleCloseEditSubscriptionModal();
    }
  }, [get('openEditModal'), get('companyId')]);

  if (isPending) return <Loader />;

  return (
    <>
      <BulkActionsModal
        companies={companies}
        handleDeactivate={(companiesIds) => handleBulkDeactivate(companiesIds)}
        isOpenModal={isOpenBulkActionsModal}
        onRemoveSelectAll={() => setSelectedCompaniesIds([])}
        selectedCompaniesIds={selectedCompaniesIds}
      />
      <ViewCompanyModal
        companyId={selectedCompanyId || ''}
        handleClose={handleCloseViewCompanyModal}
        invalidateQuery={invalidateQuery}
        isOpenModal={isOpenViewCompanyModal}
      />
      <EditSubscriptionModal
        companyId={selectedCompanyId || get('companyId') || ''}
        handleClose={handleCloseEditSubscriptionModal}
        invalidateQuery={invalidateQuery}
        isOpenModal={isOpenEditSubscriptionModal}
      />
      <CreateSubscriptionModal
        handleClose={handleCloseCreateSubscriptionModal}
        invalidateQuery={invalidateQuery}
        isOpenModal={isOpenCreateSubscriptionModal}
      />
      <CreateSubscriptionModal
        handleClose={handleCloseOfferModal}
        invalidateQuery={invalidateOffers}
        isOpenModal={isOpenOfferModal}
        mode={offerModalMode}
        offer={selectedOffer}
        onSwitchToEdit={() => setOfferModalMode('edit')}
      />
      <DeleteSubscriptionModal
        companyName={offerToDelete?.companyName}
        isOpen={!!offerToDelete}
        isPending={isDeletingOffer}
        onConfirm={handleConfirmDeleteOffer}
        onDismiss={() => setOfferToDelete(null)}
      />
      <MobileFilterModal
        handleApplyFilters={handleApplyFilters}
        handleCloseModal={handleCloseMobileFilterModal}
        handleResetFilters={handleResetFilters}
        isOpen={isOpenMobileFilterModal}
        isPaymentFilterDisabled={isSubscriptionTab}
        selectedFreqFilter={selectedFreqFilter}
        selectedPaymentFilter={selectedPaymentFilter}
        selectedPlanFilter={selectedPlanFilter}
        setSelectedFreqFilter={handleFreqFilterChange}
        setSelectedPaymentFilter={handlePaymentFilterChange}
        setSelectedPlanFilter={handlePlanFilterChange}
        toggler={togglerMobileFilterModal}
      />
      <div className="flex h-fit min-h-[400px] w-full flex-col gap-4 rounded-md lg:py-4 lg:shadow-sm">
        <CompanyHeader
          filteredCompaniesCount={filteredCompanies.length || 0}
          handleOpenMobileFilterModal={handleOpenMobileFilterModal}
          invalidateQuery={invalidateQuery}
          onCreateSubscription={handleOpenCreateSubscriptionModal}
          search={search}
          selectedFreqFilter={selectedFreqFilter}
          selectedPaymentFilter={selectedPaymentFilter}
          selectedPlanFilter={selectedPlanFilter}
          selectedTab={selectedTab}
          setCurrentPage={setCurrentPage}
          setSearch={handleSearchChange}
          setSelectedFreqFilter={handleFreqFilterChange}
          setSelectedPaymentFilter={handlePaymentFilterChange}
          setSelectedPlanFilter={handlePlanFilterChange}
          setTab={handleTabChange}
        />
        {isMobile && <div className="h-[1px] w-full bg-gray-100" />}
        <div
          className="flex h-full w-full flex-col justify-between gap-4"
          style={{ minHeight: !isMobile ? 'calc(100vh - 216px)' : undefined }}
        >
          {isSubscriptionTab ? (
            <SubscriptionTab
              currentPage={currentPage}
              handleDelete={(offer) => setOfferToDelete(offer)}
              handleEdit={handleEditOffer}
              handlePageChange={setCurrentPage}
              handleSelectNumberOfFetchedItems={handleSelectNumberOfFetchedItems}
              handleView={handleViewOffer}
              isMobile={isMobile}
              numberOfFetchedItems={numberOfFetchedItems}
              offers={offers}
              onSortClick={handleOfferSortField}
              sortField={offerSortState.field}
              sortMode={offerSortState.sortMode}
              totalPages={offerTotalPages}
            />
          ) : (
            <CompanyTab
              companies={selectedTab === CompanyTabType.ALL ? companies : filteredCompanies}
              currentPage={currentPage}
              handleDeactivate={(companyId) => {
                deactivateCompany(
                  { companyId },
                  {
                    onSuccess: () => {
                      invalidateQuery();
                      setSelectedCompanyId(null);
                      setSelectedCompaniesIds([]);
                      toast.success('Company has been deactivated');
                    },
                  },
                );
              }}
              handleEdit={(companyId) => {
                setSelectedCompanyId(companyId);
                handleOpenEditSubscriptionModal();
              }}
              handlePageChange={setCurrentPage}
              handleSelectNumberOfFetchedItems={handleSelectNumberOfFetchedItems}
              handleView={(companyId) => {
                setSelectedCompanyId(companyId);
                handleOpenViewCompanyModal();
              }}
              isMobile={isMobile}
              isSelected={(selectedId) => {
                return !!selectedCompaniesIds.find((id) => id === selectedId);
              }}
              isSelectStarted={selectedCompaniesIds.length !== 0}
              numberOfFetchedItems={numberOfFetchedItems}
              onRemoveSelect={(selectedId) =>
                setSelectedCompaniesIds((prev) => prev.filter((id) => id !== selectedId))
              }
              onRemoveSelectAll={() => setSelectedCompaniesIds([])}
              onSelect={(companyId) => {
                setSelectedCompaniesIds((prev) => [...prev, companyId]);
              }}
              onSelectAll={(companiesIds) => setSelectedCompaniesIds(companiesIds)}
              onSortClick={(newField) => handleSortField(newField)}
              sortField={sortState.field}
              sortMode={sortState.sortMode}
              totalPages={selectedTab === CompanyTabType.ALL ? totalPages : filteredTotalPages}
            />
          )}
        </div>
      </div>
    </>
  );
};
export default Companies;
